import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Download, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { botService } from '../../services/botService';

const Collection = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    loadCollectionBots();
  }, [currentPage, sortBy, sortDir]);

  const loadCollectionBots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await botService.getCollectionBots(currentPage, 12, sortBy, sortDir);
      setBots(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Error loading collection bots:', error);
      setError('Failed to load collection bots. Please try again.');
      setBots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadCollectionBots();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await botService.searchPublicBots(searchTerm);
      setBots(response || []);
      setTotalPages(0);
      setTotalElements(response?.length || 0);
    } catch (error) {
      console.error('Error searching bots:', error);
      setError('Failed to search bots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToMyList = async (botId) => {
    try {
      await botService.addBotToCustomer(botId);
      // Show success message or update UI
      console.log('Bot added to your list successfully');
    } catch (error) {
      console.error('Error adding bot to list:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add bot to your list. Please try again.';
      setError(errorMessage);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setCurrentPage(0);
  };

  const filteredBots = bots.filter(bot =>
    bot.botName && bot.botName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const normalizedStatus = status === '10001' ? 'DRAFT' : 
                            status === '10002' ? 'PUBLISHED' : 
                            status === '10003' ? 'ARCHIVED' : status;
    
    switch (normalizedStatus) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collection</h1>
          <p className="text-gray-600">Discover and explore public bots from the community</p>
        </div>
        <div className="text-sm text-gray-500">
          {totalElements} bots available
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search bots..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleSearch}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Search
        </button>
      </div>

      {/* Sort Options */}
      <div className="mb-6 flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 mr-2">Sort by:</span>
        {[
          { field: 'createdAt', label: 'Date Created' },
          { field: 'botName', label: 'Name' },
          { field: 'updatedAt', label: 'Last Updated' }
        ].map(({ field, label }) => (
          <button
            key={field}
            onClick={() => handleSort(field)}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              sortBy === field
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label} {sortBy === field && (sortDir === 'asc' ? '↑' : '↓')}
          </button>
        ))}
      </div>

      {/* Bots Grid */}
      <div className="flex-1 flex flex-col">
        {loading ? (
          <div className="flex-1 flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="flex-1">
            {filteredBots.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredBots.map((bot) => (
                  <div key={bot.botId} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{bot.botName}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{bot.description || 'No description'}</p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bot.status)}`}>
                          {bot.status === '10001' ? 'DRAFT' : 
                           bot.status === '10002' ? 'PUBLISHED' : 
                           bot.status === '10003' ? 'ARCHIVED' : bot.status}
                        </span>
                      </div>
                    </div>

                    <div className="text-sm text-gray-500 mb-3">
                      <p>Created: {new Date(bot.createdAt).toLocaleDateString()}</p>
                      <p>Updated: {new Date(bot.updatedAt).toLocaleDateString()}</p>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleAddToMyList(bot.botId)}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm flex items-center justify-center space-x-1 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add to My List</span>
                      </button>
                      
                      <button
                        onClick={() => navigate(`/bots/builder/${bot.botId}`)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col justify-center items-center">
                <div className="text-gray-400 mb-4">
                  <Star className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No bots found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'No bots match your search.' : 'No public bots are available at the moment.'}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      loadCollectionBots();
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(0, Math.min(totalPages - 1, currentPage - 2 + i));
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-md text-sm ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage === totalPages - 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Collection;
